/**
 * Site setup — parte UNIVERSAL (roda no servidor e no cliente).
 *
 * O que é só do servidor mora em `./setup/server.ts`, importado apenas pelo
 * `worker-entry.ts`. A divisão não é organização: `src/router.tsx` importa
 * ESTE arquivo, e o router é o grafo do bundle do CLIENTE — tudo que este
 * módulo alcançar é baixado e avaliado por todo visitante.
 *
 * Medido (Lighthouse + sourcemap do build, preview deste PR): com tudo junto
 * aqui, o chunk `vendor-router` do cliente ia a 1,3 MB cru / 404 KB de
 * transferência e aparecia como long task de 402 ms na janela de TBT. A
 * atribuição por sourcemap mostrava o grafo de SERVIDOR inteiro lá dentro:
 *
 *   @decocms/apps-vtex            324 KB   (loaders de catálogo VTEX)
 *   node-html-parser + entities   375 KB   (parser de HTML do framework)
 *   postcss                       105 KB   (build-time!)
 *   cms/resolve.ts                 95 KB
 *   schemas.gen.ts                 72 KB
 *   setup/commerce-loaders.ts      38 KB
 *   cms/schema.ts                  40 KB
 *   middleware/observability.ts    29 KB
 *
 * Nada disso roda no browser: loader resolve no worker, o cliente fala com o
 * servidor por `POST /deco/invoke`. Mesmo achado que o zeedog documentou
 * ("tira o grafo de servidor do bundle do cliente").
 *
 * ORDEM — CRÍTICO, não chame nada deste arquivo no top-level (import time):
 * a versão original (monolítica) chamava tudo numa ÚNICA sequência —
 * createSiteSetup → createAdminSetup → setVtexFetch → setAsyncRenderingConfig
 * → applySectionConventions → registerCommerceLoaders → registerLayoutSections.
 * A primeira versão desta divisão quebrou isso: este arquivo executava
 * `setAsyncRenderingConfig`/`applySectionConventions`/`registerLayoutSections`
 * no próprio import, e `setup/server.ts` importava este arquivo PRIMEIRO —
 * invertendo a ordem, essas três rodavam ANTES de `createSiteSetup`.
 *
 * Resultado, bisecado ao vivo com 3 preview builds (main funcionando vs PR
 * quebrado vs PR com o setup revertido pra monolítico): as sections globais
 * `Modal/Minicart` e `Modal/LowStockModal` paravam de renderizar — ficavam
 * nos `rawRefs` do `resolveSiteGlobals` (dados chegavam) mas nunca viravam
 * `<section>` na árvore resolvida (sumiam do DOM). Sintoma reportado: clicar
 * "Comprar" numa prateleira não abria o drawer do carrinho.
 *
 * Fix: NENHUMA chamada roda no import deste arquivo. `applyUniversalSetup()`
 * é uma função que `setup/server.ts` CHAMA no ponto certo da sequência
 * original (depois de `createSiteSetup`/`createAdminSetup`/`setVtexFetch`),
 * e que `router.tsx` (cliente, sem o resto do server) chama sozinha.
 *
 * REGRA ao mexer aqui: só entra neste arquivo o que o CLIENTE precisa para
 * hidratar e navegar. Qualquer coisa que toque decofile, loader, VTEX, admin
 * ou cache de edge vai para `./setup/server.ts`. E nenhuma chamada nova pode
 * rodar fora de `applyUniversalSetup()`.
 *
 * Section metadata (eager, sync, layout, cache, LoadingFallback) é declarada
 * em cada section e extraída por generate-sections.ts.
 */

import "./setup/errorLogging";

import {
  applySectionConventions,
  registerLayoutSections,
  registerSections,
  setAsyncRenderingConfig,
} from "@decocms/blocks/cms";
import { sectionMeta, syncComponents, loadingFallbacks } from "../.deco/sections.gen";
import { registerEmptyDeferredFallbacks } from "./setup/deferredSectionFallback";

// Puxa os `createServerFn` do framework para o grafo de origem do usuário,
// para que entrem no manifest do TanStack. Sem isto o loader de section
// deferida some do manifest e devolve HTTP 500 — por isso fica no lado
// UNIVERSAL mesmo sendo código de servidor: quem varre o grafo para montar o
// manifest é o build do cliente/rotas.
import "./server/serverFns";

/**
 * Único `import.meta.glob` das sections, exportado para `setup/server.ts`
 * reusar em `createSiteSetup({ sections })`.
 *
 * CRÍTICO — segunda causa real do bug do carrinho (a primeira foi a ORDEM,
 * ver acima): `import.meta.glob` é macro do Vite resolvida em build-time, e
 * as chaves do objeto que ele devolve são o padrão relativo AO ARQUIVO QUE
 * CHAMA, não ao alvo. `setup.ts` chamando `import.meta.glob("./sections/**")`
 * e `setup/server.ts` chamando `import.meta.glob("../sections/**")` —
 * mesmo arquivo físico, profundidades diferentes — geravam DUAS chaves
 * diferentes para a MESMA section (`./sections/Modals/Minicart.tsx` vs
 * `../sections/Modals/Minicart.tsx`). `createSiteSetup` registra a árvore de
 * sections com as chaves de UMA chamada; `applySectionConventions` cruza
 * `sectionMeta`/`syncComponents` (de `.deco/sections.gen.ts`, formato fixo)
 * contra as chaves da OUTRA chamada — descasamento silencioso para as
 * sections cujo metadata dependia desse cruzamento, sem erro nenhum (dado
 * chegava em `rawRefs`, nunca virava `<section>` resolvida). Confirmado:
 * bisect com a ORDEM já corrigida mas os dois globs ainda separados
 * continuou quebrado; só normalizou ao reusar UM glob nos dois lugares.
 * Nunca declare um segundo `import.meta.glob` de sections neste projeto —
 * sempre importe este.
 */
export const SECTION_GLOB = import.meta.glob("./sections/**/*.{ts,tsx}") as Record<
  string,
  () => Promise<any>
>;

let appliedConventions = false;
let appliedLayout = false;

/**
 * Chamado por `router.tsx` (cliente) diretamente, e por `setup/server.ts`
 * (servidor) DEPOIS de `createSiteSetup()`/`createAdminSetup()`/
 * `setVtexFetch()` — nunca antes, ver cabeçalho. Duas funções, não uma só,
 * para o servidor poder intercalar `registerCommerceLoaders`/
 * `setInvokeLoaders` ENTRE elas — exatamente a ordem que a versão
 * monolítica original tinha (`applySectionConventions` antes dos commerce
 * loaders, `registerLayoutSections` depois). O cliente chama as duas em
 * sequência; para ele a ordem entre si não importa (não há commerce loaders
 * no cliente).
 */
export function applyAsyncRenderingAndConventions(): void {
  if (appliedConventions) return;
  appliedConventions = true;

  // Deferência below-the-fold: por POSIÇÃO, não pelo ⚡ do editor.
  //
  // `respectCmsLazy: false` ignora os 718 wrappers
  // `website/sections/Rendering/Lazy.tsx` do decofile (98 blocos). Não é
  // preferência: em `shouldDeferSection` (resolve.ts) o check do ⚡ roda ANTES
  // de `isLayoutSection`/`neverDefer`/`eager`, e aqui o editor embrulhou em
  // Lazy justamente o `Header - 01` e o `SearchResult` da PLP. Ligar
  // `respectCmsLazy` deferiria o header e a grid de produtos — LCP e SEO da
  // PLP — sem nenhuma forma de forçar eager por código.
  //
  // `foldThreshold: 6` defere qualquer section com índice >= 6. Na PDP isso
  // tira ProductDescription, as 3 shelves relacionadas e ProductBlogPosts do
  // payload inicial; o BuyTogether cai no índice 5 e precisa do
  // `export const deferred = true` na própria section (ver BuyTogether.tsx).
  // Acima da dobra (PageSEO, Header, Benefícios, Barra de Frete,
  // ProductDetails) fica eager por estar no índice < 6, e Header/Footer/Theme
  // também por serem layout. O `SearchResult` da PLP cai no índice 6 e só
  // continua eager por causa do `export const neverDefer = true`.
  //
  // COMO a section deferida chega no cliente: `IntersectionObserver` +
  // `POST loadDeferredSection`, o único caminho que existe. O caminho de
  // streaming do TanStack (`deferredPromises`) foi REMOVIDO do framework de
  // propósito — `cmsRoute.ts` documenta que ele resolvia todas as sections
  // deferidas no servidor e mandava as imagens de todas as vitrines de uma vez
  // — e nem o framework nem este site constroem esse objeto em lugar nenhum.
  // Medido: navegação SPA para a PDP faz 1 GET de 1132KB + 10 POSTs conforme o
  // scroll (antes de decocms/blocks#506, 1 GET bloqueante de 4178KB).
  setAsyncRenderingConfig({ respectCmsLazy: false, foldThreshold: 6 });

  // Registro do glob INTEIRO — no cliente ninguém mais faz isso.
  //
  // `createSiteSetup({ sections: SECTION_GLOB })` roda só em `setup/server.ts`,
  // então o registry do SERVIDOR tem todas as sections e o do CLIENTE só as que
  // `applySectionConventions` registra: `sync`, `clientOnly` e as que exportam
  // `LoadingFallback`. Uma section eager sem nenhum dos três existe no SSR e
  // NÃO existe na hidratação — `getLazyComponent` devolve null e o
  // `DecoPageRenderer` faz `return null`, sumindo com o `<section>` inteiro.
  //
  // O efeito não é a section faltando e pronto: a lista de filhos desanda a
  // partir dali e o React aborta a hidratação com o erro #418 ("server rendered
  // HTML didn't match the client") em TODA página que tenha uma dessas.
  // Medido na home: o servidor emite `Modals-LowStockModal` no índice 4, o
  // cliente põe `Content-BenefitsSection` no lugar, e o #418 aparece no console
  // de 18 páginas da run de paridade de 21/09/2026.
  //
  // Registrar aqui não pesa no bundle: `SECTION_GLOB` já está no grafo do
  // cliente (é o mesmo objeto passado abaixo em `sectionGlob`) e os valores são
  // `() => import(...)` preguiçosos — nenhum módulo é avaliado no registro.
  // Vai ANTES de `applySectionConventions` de propósito: `registerSection`
  // sobrescreve as options, e as de convenção (loadingFallback/clientOnly)
  // precisam ganhar.
  const globSections: Record<string, () => Promise<any>> = {};
  for (const [path, loader] of Object.entries(SECTION_GLOB)) {
    globSections[`site/${path.slice(2)}`] = loader;
  }
  registerSections(globSections);

  // -- Convention-driven section registration --
  applySectionConventions({
    meta: sectionMeta,
    syncComponents,
    loadingFallbacks,
    sectionGlob: SECTION_GLOB,
  });

  // Depois das convenções: quem saiu delas sem options ganha um
  // LoadingFallback vazio, igual no SSR e na hidratação. Sem isto, a section
  // deferida desenha 192px de skeleton no SSR de um isolate que já carregou o
  // módulo e nada no cliente, e o React aborta a hidratação com o #418. Ver
  // `setup/deferredSectionFallback.ts`.
  registerEmptyDeferredFallbacks(globSections);
}

/**
 * Chamado DEPOIS de `applyAsyncRenderingAndConventions()` — no servidor,
 * também depois de `registerCommerceLoaders`/`setInvokeLoaders`, igual à
 * ordem original. Ver o comentário de `applyAsyncRenderingAndConventions`.
 */
export function applyLayoutSections(): void {
  if (appliedLayout) return;
  appliedLayout = true;

  // Header e Footer injetam `isMobile` por request via section-loaders. Até o
  // 7.62.0 o cache de layout — o `layoutCache` de `cms/sectionLoaders.ts`, que
  // guarda a saída do LOADER, onde o `isMobile` nasce — era chaveado só pelo
  // caminho do componente, então cacheá-los servia a variante do primeiro
  // visitante para todo mundo por 5min. Medido aqui na PDP: desktop primeiro e o
  // celular recebia `h-[90px]`; mobile primeiro e o desktop recebia
  // `id="header-mobile-menu"`.
  //
  // O decocms/blocks#530 pôs device na chave (`layoutLoaderCacheKey`), e o 7.62.1
  // traz. Verificado alternando requests: DESKTOP / MOBILE / DESKTOP / MOBILE.
  //
  // O FOOTER fica de fora, por medição e não por risco. Ele está no índice 14 da
  // PDP, acima do `foldThreshold: 6`, então hoje é DEFERIDO. Registrar como layout
  // o torna eager (`shouldDeferSection` devolve false para layout) e o markup dele
  // sai do POST e entra no HTML inicial: +24 KB em toda página, uma section
  // deferida a menos. Três arranjos medidos na mesma bateria:
  //
  //   só Theme                PDP 175,8 KB / 10 def   PLP 770,3 KB / 4 def
  //   + Header                PDP 175,8 KB / 10 def   PLP 770,4 KB / 4 def
  //   + Header + Footer       PDP 200,0 KB /  9 def   PLP 794,7 KB / 3 def
  //
  // Header é de graça: ele já é eager (índice 1), então registrar não muda byte
  // nem deferência — só para de re-resolver o bloco em toda página e navegação.
  // O Footer troca 24 KB de HTML acima da dobra por um round-trip a menos numa
  // section que ninguém vê sem rolar até o fim. Se algum dia o Footer virar eager
  // por outro motivo, aí vale reavaliar.
  registerLayoutSections([
    "site/sections/Theme/Theme.tsx",
    "site/sections/Header/Header.tsx",
  ]);
}
